import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Icon from "@ant-design/icons";
import { Button, notification } from "antd";
import Header from "../../components/layout/header";
import SuccessAlert from "../../components/mobile/SuccessAlert";
import Styles from "./registerTrolleyError.module.scss";
import { LeftIcon } from "../../assets/icons/icons";
import { api } from "../../services/api";

const TrolleyLocationWarning = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const trolleyId = searchParams.get("trolleyId") || "";
  const locationBarcode = searchParams.get("locationBarcode") || "";
  const primaryLocation = searchParams.get("primaryLocation") || "";
  const secondaryLocation = searchParams.get("secondaryLocation") || "";
  const fromLocation = searchParams.get("from") || "trolley"; // "trolley" or "location"
  
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<React.ReactNode>("");

  const handleContinue = async () => {
    if (!trolleyId || !locationBarcode) {
      notification.error({
        message: "Missing Information",
        description: "Unable to update location. Please try again.",
      });
      return;
    }

    setIsUpdating(true);
    
    try {
      // Make the API call to update trolley location with OUT action
      const response = await api.updateTrolleyLocation(trolleyId, locationBarcode, "Out");
      
      // Parse the API response message
      const apiMessage = response?.message || `Trolley ${trolleyId} successfully dispatched from ${primaryLocation}. Totes have been unloaded and marked as COMPLETED.`;
      
      // Show success alert popup with API message
      setSuccessMessage(apiMessage);
      setIsSuccessOpen(true);
    } catch (error: any) {
      console.error("Failed to update trolley location:", error);
      notification.error({
        message: "Update Failed",
        description: error?.response?.data?.detail || error?.message || "We couldn't update the trolley location. Please try again.",
        duration: 5,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSuccessClose = () => {
    setIsSuccessOpen(false);
    
    // Navigate back to the appropriate page
    if (fromLocation === "location") {
      // If came from location page, go back to location page
      navigate(`/location-info/${encodeURIComponent(locationBarcode)}`);
    } else {
      // Default: go back to trolley info page
      navigate(`/trolley-info/${encodeURIComponent(trolleyId)}`);
    }
  };

  const handleBack = () => {
    // Go back without making any changes
    if (fromLocation === "location") {
      navigate(`/location-info/${encodeURIComponent(locationBarcode)}`);
    } else {
      navigate(`/trolley-info/${encodeURIComponent(trolleyId)}`);
    }
  };

  return (
    <div className={Styles.mainContainer}>
      <Header />
      <div className={Styles.contentContainer}>
        <div className={Styles.header}>
          <Icon component={LeftIcon} onClick={handleBack} />
          <h2 className={Styles.title}>Update Location</h2>
        </div>

        <div className={Styles.errorSection}>
          <div className={Styles.errorMessage} style={{ color: "#FF3B30" }}>
            Warning: Trolley Not Empty
          </div>

          <div className={Styles.errorMessage} style={{ color: "#FF3B30", fontSize: "16px", marginTop: "16px" }}>
            The Trolley (ID: {trolleyId}) isn't empty. Totes are still loaded.
          </div>

          <div className={Styles.promptMessage}>
            Would you like to continue with the Location OUT? The Totes will be automatically unloaded and moved to the COMPLETED state.
          </div>

          <Button
            className={Styles.continueButton}
            size="large"
            onClick={handleContinue}
            loading={isUpdating}
            disabled={isUpdating}
          >
            Continue
          </Button>
        </div>
      </div>

      <SuccessAlert
        open={isSuccessOpen}
        title="Trolley Location Updated"
        message={successMessage}
        buttonText="Done"
        onClose={handleSuccessClose}
      />
    </div>
  );
};

export default TrolleyLocationWarning;

